import type { ComponentType, ReactNode } from 'react';
import { cn } from '~/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';

export interface SegmentedOption<T extends string = string> {
  value: T;
  label: ReactNode;
  icon?: ComponentType<{ size?: number }>;
  /** FRONT-2410: an option that cannot be chosen right now (the active one stays shown as active). */
  disabled?: boolean;
  /**
   * Hover hint for this option (the design-system tooltip). Carried on a wrapper
   * span so a `disabled` option, which swallows pointer events, still shows it.
   * An option that DECLARES the key (even as `undefined`) always renders the same
   * wrapper tree and only the tooltip text comes and goes, so a focused tab is never
   * remounted when its hint appears or disappears. Keep the key ALWAYS present on such an option: the stability depends on it.
   */
  tooltip?: string;
  /** Optional `data-testid` for this option's button. */
  testId?: string;
  /** Accessible name when the label alone does not say it (e.g. a label that carries a count pill). */
  ariaLabel?: string;
}

interface SegmentedProps<T extends string = string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** `sm` shrinks the pill padding/text for a secondary toggle sitting next
   *  to a primary one (e.g. the Work board's Active/Inactive filter next to
   *  its Projects/Routines tab). Default: full size. */
  size?: 'default' | 'sm';
  className?: string;
  'aria-label'?: string;
}

/**
 * The app's one shared segmented-control (pill-group toggle) primitive —
 * added for the Work board's Projects/Routines + Active/Inactive toggles
 * (FRONT-1003) so new surfaces that need this pattern reach for a shared
 * component instead of hand-rolling one. Same visual language (track:
 * `bg-muted p-1`; active pill: `bg-card` + `border` + `shadow-sm`) as the
 * local `Segmented` in `features/chat/reading-prefs.tsx`, which predates
 * this component and has NOT been migrated to it — that copy still exists
 * as its own implementation, not an alias of this one.
 */
export function Segmented<T extends string = string>({
  options,
  value,
  onChange,
  size = 'default',
  className,
  'aria-label': ariaLabel,
}: SegmentedProps<T>) {
  // FRONT-3223/FRONT-3314: the active pill keeps the others' weight (a bolder
  // label is wider and nudged the row).
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center gap-1 rounded-[12px] border border-border bg-muted p-1',
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        const Icon = option.icon;
        const button = (
          <button
            key={'tooltip' in option ? undefined : option.value}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={option.disabled}
            data-testid={option.testId}
            aria-label={option.ariaLabel}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-[9px] border border-transparent font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              size === 'sm' ? 'px-3 py-1 text-xs' : 'px-3.5 py-1.5 text-sm',
              active
                ? 'border-border bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {Icon && <Icon size={size === 'sm' ? 12 : 13} />}
            {option.label}
          </button>
        );
        if (!('tooltip' in option)) return button;
        return (
          <Tooltip key={option.value}>
            <TooltipTrigger asChild>
              <span className="inline-flex">{button}</span>
            </TooltipTrigger>
            {option.tooltip ? <TooltipContent>{option.tooltip}</TooltipContent> : null}
          </Tooltip>
        );
      })}
    </div>
  );
}
